/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, Feb 2023
 * @flow
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classes from './CreateBooking.module.css';
import Button from '../../../../components/Button';
import strings from '../../../../globalization';
import TextInput from '../../../../components/TextInput';
import { CountryPicker, getPickerData } from "../../../../components/CountryPicker";
import FireboardUtils from '../../../../util/fireboard-util';
import StepInput from '../StepInput';

const EmailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const defaultInputFields = {
  nameEnabled: true,
  nameRequired: true,
  phoneNumberEnabled: true,
  phoneNumberRequired: true,
  emailAddressEnabled: true,
  emailAddressRequired: true,
};

const CreateBooking = ({ onClosePopup, onCreateBooking }) => {
  const [selectedCountry, setSelectedCountry] = useState({});
  const [state, setState] = useState({
    phoneNumber: '',
    name: '',
    emailAddress: '',
    partySize: '2',
    note: '',
    staffComment: '',
  });

  const { storeInfo, deviceApi, currencyInfo } = useSelector(state => state);
  const { deviceGroup } = deviceApi?.data || {};
  const {
    nameEnabled,
    nameRequired,
    phoneNumberEnabled,
    phoneNumberRequired,
    emailAddressEnabled,
    emailAddressRequired,
  } = deviceGroup?.inputFields || defaultInputFields;
  const { iso2 } = currencyInfo || {};

  useEffect(() => {
    setDefaultCountry();
  }, []);

  const setDefaultCountry = () => {
    const countriesData = getPickerData();
    const defaultCountry = countriesData.find(country => country.iso2 === String(iso2).toLowerCase());
    setSelectedCountry(defaultCountry);
  };

  const onCountryChange = country => {
    const formattedPhoneNumber = FireboardUtils.inputFormattedPhnNum(state.phoneNumber, country.iso2);
    setSelectedCountry(country);
    setState({
      ...state,
      phoneNumber: formattedPhoneNumber,
    });
  };

  const onChangeInput = (key, value) => {
    if (key === 'note') {
      setState({
        ...state,
        [key]: document.getElementById("noteArea").value,
      })
    } else if (key === 'staffComment') {
      setState({
        ...state,
        [key]: document.getElementById("staffCommentArea").value,
      })
    } else {
      setState({
        ...state,
        [key]: value,
      })
    }
  };

  const onSubmit = () => {
    try {
      const { id: storeId } = storeInfo?.data || {};
      const payload = {
        countryCode: state.phoneNumber ? selectedCountry.dialCode.split('+')[1] : '',
        phoneNumber: state.phoneNumber ? FireboardUtils.inputUnFormattedPhnNum(state.phoneNumber, selectedCountry.iso2) : '',
        name: state.name.trim() || '',
        emailAddress: state.emailAddress.trim() || '',
        note: state.note.trim() || '',
        comment: state.staffComment.trim() || '',
        partySize: +state.partySize,
        businessDate: new Date().toISOString(),
        bookingDate: new Date().toISOString(),
        completeDate: null,
        status: 0,
        stationId: null,
        memberId: null,
        storeId,
        id: 0,
      };
      onCreateBooking && onCreateBooking(payload);
    } catch (error) {
      console.error(error);
    }
  };

  const isSubmitDisabled = () => {
    let isEmailValid = true, isNameValid = true, isPhoneNumberValid = true;
    if (emailAddressEnabled && (emailAddressRequired || (!emailAddressRequired && state.emailAddress))) {
      isEmailValid = EmailRegex.test(state.emailAddress.trim().toLowerCase());
    }
    if (nameEnabled && (nameRequired || (!nameRequired && state.name))) {
      isNameValid = ((state.name).trim()).length > 0;
    }
    if (phoneNumberEnabled && (phoneNumberRequired || (!phoneNumberRequired && state.phoneNumber))) {
      isPhoneNumberValid = (FireboardUtils.inputUnFormattedPhnNum(state.phoneNumber, selectedCountry.iso2)).length >= 10;
    }
    const isPartySizeValid = state.partySize.length > 0;
    return !(isEmailValid && isNameValid && isPhoneNumberValid && isPartySizeValid);
  };

  return (
    <div>
      <div className={classes.headerWrapper}>
        <div className={classes.headerTxt}>{strings.createWaitList}</div>
        <div className={classes.closeBtn} onClick={onClosePopup}>X</div>
      </div>
      <div className={classes.contentWrapper}>
        {phoneNumberEnabled && (
          <React.Fragment>
            <div>
              <label className={classes.label}>{strings.countryCode}</label>
              <CountryPicker
                selectedCountry={selectedCountry}
                onCountryChange={onCountryChange}
                containerStyle={classes.input}
              />
            </div>
            <TextInput
              placeholder={`${strings.placeholderPhone} ${phoneNumberRequired ? '' : '(optional)'}`}
              type='tel'
              value={FireboardUtils.inputFormattedPhnNum(state.phoneNumber, selectedCountry.iso2)}
              onChange={value => onChangeInput('phoneNumber', value)}
              inputStyle={classes.input}
            />
          </React.Fragment>
        )}
        {nameEnabled && (
          <TextInput
            placeholder={`${strings.nameLabel} ${nameRequired ? '' : '(optional)'}`}
            type='text'
            value={state.name}
            onChange={value => onChangeInput('name', value)}
            inputStyle={classes.input}
          />
        )}
        <StepInput
          placeholder={strings.partySize}
          type='number'
          value={state.partySize}
          onChange={value => onChangeInput('partySize', value)}
          inputStyle={classes.input}
        />
        {emailAddressEnabled && (
          <TextInput
            placeholder={`${strings.emailAddressPlaceholder} ${emailAddressRequired ? '' : '(optional)'}`}
            type='text'
            value={state.emailAddress}
            onChange={value => onChangeInput('emailAddress', value)}
            inputStyle={classes.input}
          />
        )}
        <div className={classes.noteContainer}>
          <label className={classes.label}>{`${strings.noteLabel} (optional)`}</label>
          <textarea
            rows={3}
            maxLength={100}
            id="noteArea"
            value={state.note}
            onChange={value => onChangeInput('note', value)}
            className={classes.textarea}
          ></textarea>
        </div>
        <div className={classes.noteContainer}>
          <label className={classes.label}>{`${strings.staffComment} (optional)`}</label>
          <textarea
            rows={3}
            maxLength={100}
            id="staffCommentArea"
            value={state.staffComment}
            onChange={value => onChangeInput('staffComment', value)}
            className={classes.textarea}
          ></textarea>
        </div>
      </div>
      <div className={classes.buttonsWrapper}>
        <Button className={classes.button} onClick={onClosePopup}>
          <div className={classes.buttonText}>{strings.cancel}</div>
        </Button>
        <Button className={classes.button} onClick={onSubmit} disabled={isSubmitDisabled()}>
          <div className={classes.buttonText}>{strings.submit}</div>
        </Button>
      </div>
    </div>
  );
};

export default CreateBooking;

CreateBooking.propTypes = {
  onClosePopup: PropTypes.func,
  onCreateBooking: PropTypes.func,
};

CreateBooking.defaultProps = {
  onClosePopup: () => { },
  onCreateBooking: () => { },
};
